import type { ServiceResponse } from './types';
import type { CartItem } from '../types/Product';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response types
interface AbandonedCartItemDto {
  productId: string;
  quantity: number;
  price: number;
  addedAt: string;
}

interface AbandonedCartResponseDto {
  _id: string;
  customerId: string;
  items: AbandonedCartItemDto[];
  totalAmount: number;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
}

// Frontend types
export interface AbandonedCart {
  customerId: string;
  items: CartItem[];
  totalAmount: number;
  lastUpdated: Date;
}

export interface CartUpdateRequest {
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
  }>;
}

class AbandonedCartService {
  // Get abandoned cart for current customer
  async getMyCart(): Promise<ServiceResponse<AbandonedCart | null>> {
    try {
      // No need to get user ID from localStorage, JWT will handle authentication
      const response = await apiRequest<AbandonedCartResponseDto>(
        API_ENDPOINTS.ABANDONED_CART.MY_CART,
        {},
        true
      );

      if (!response || !response.items || response.items.length === 0) {
        return {
          success: true,
          data: null
        };
      }

      // Note: We'll need to fetch product details separately
      // For now, return basic cart structure
      return {
        success: true,
        data: {
          customerId: response.customerId,
          items: response.items as unknown as CartItem[], // Type assertion for now
          totalAmount: response.totalAmount,
          lastUpdated: new Date(response.lastUpdated)
        }
      };
    } catch (error) {
      // If 404, cart doesn't exist
      if (error instanceof Error && error.message.includes('404')) {
        return {
          success: true,
          data: null
        };
      }

      console.error('Error fetching abandoned cart:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch cart'
      };
    }
  }

  // Update abandoned cart
  async updateCart(items: CartItem[]): Promise<ServiceResponse<void>> {
    try {
      const request: CartUpdateRequest = {
        items: items.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price
        }))
      };

      await apiRequest<void>(
        API_ENDPOINTS.ABANDONED_CART.UPDATE_MY_CART,
        {
          method: 'PUT',
          body: JSON.stringify(request),
        },
        true
      );

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      console.error('Error updating abandoned cart:', error);
      return {
        success: false,
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to update cart'
      };
    }
  }

  // Clear abandoned cart
  async clearCart(): Promise<ServiceResponse<void>> {
    try {
      await apiRequest<void>(
        API_ENDPOINTS.ABANDONED_CART.CLEAR_MY_CART,
        {
          method: 'DELETE',
        },
        true
      );

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      console.error('Error clearing abandoned cart:', error);
      return {
        success: false,
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to clear cart'
      };
    }
  }

  // Sync local cart with backend
  async syncCart(localCart: CartItem[]): Promise<ServiceResponse<void>> {
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      if (!user.id) {
        // User not authenticated, keep local cart only
        return {
          success: true,
          data: undefined
        };
      }

      // Get backend cart
      const backendCartResponse = await this.getMyCart();
      
      // If there's a backend cart and local cart is empty, don't overwrite
      if (backendCartResponse.success && backendCartResponse.data && localCart.length === 0) {
        return {
          success: true,
          data: undefined,
          message: 'Backend cart preserved'
        };
      }

      // If local cart has items, update backend
      if (localCart.length > 0) {
        return await this.updateCart(localCart);
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      console.error('Error syncing cart:', error);
      // Don't fail silently, but don't break the app either
      return {
        success: true,
        data: undefined
      };
    }
  }

  // Check if cart should be restored
  shouldRestoreCart(): boolean {
    const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
    return !!user.id;
  }
}

export const abandonedCartService = new AbandonedCartService();